import { createSelector } from '@app/utils/createSelector'
import { mergeMaps } from '@app/utils/mergeMaps'
import { normalizeSafe } from '@app/utils/normalizer'

import type { ApiAvatar, ApiCountry, ApiInaccurateLocation, ApiLocality, ApiLocation, ApiPerk, ApiPlace } from '@app/packages/api/v2/types/entities'

import { createReduxSlice } from '@app/store/redux_slice'

/** Every key optional: a store only holds the types some response happened to carry. */
export type EntitiesState = {
  avatars?: { [id: string]: ApiAvatar }
  countries?: { [id: string]: ApiCountry }
  inaccurate_locations?: { [id: string]: ApiInaccurateLocation }
  localities?: { [id: string]: ApiLocality }
  locations?: { [id: string]: ApiLocation }
  perks?: { [id: string]: ApiPerk }
  places?: { [id: string]: ApiPlace }
}

const entitiesSlice = createReduxSlice<EntitiesState>('entities')

export const entitiesSelector = createSelector([entitiesSlice.selector], entities => entities ?? EMPTY_ENTITIES)

export const setEntitiesSlice = (entities: EntitiesState) => entitiesSlice.set(entities)

export const avatarsSelector = createSelector([entitiesSelector], entities => entities.avatars ?? {})
export const countriesSelector = createSelector([entitiesSelector], entities => entities.countries ?? {})
export const inaccurateLocationsSelector = createSelector([entitiesSelector], entities => entities.inaccurate_locations ?? {})
export const localitiesSelector = createSelector([entitiesSelector], entities => entities.localities ?? {})
export const locationsSelector = createSelector([entitiesSelector], entities => entities.locations ?? {})
export const perksSelector = createSelector([entitiesSelector], entities => entities.perks ?? {})
export const placesSelector = createSelector([entitiesSelector], entities => entities.places ?? {})

/**
 * Merges in the entities a response carried along, keyed by their JSON:API type. Called by
 * whatever made the request rather than observed from a case handler, so an endpoint contributes
 * its entities without any slice naming it — and a `fields[…]`-trimmed variant cannot land here,
 * because those carry a type of their own.
 */
export const updateEntitiesSlice = (payload: unknown) =>
  entitiesSlice.update(state => {
    const normalized = normalizeSafe<Entity>(payload)
    let next = state ?? EMPTY_ENTITIES
    for (const type of ENTITY_TYPES) {
      const incoming = normalized[type]
      if (!incoming) continue
      const current = next[type] ?? {}
      const merged = mergeMaps<Entity>(current, incoming)
      if (merged === current) continue
      next = { ...next, [type]: merged }
    }
    return next
  })

type Entity = ApiAvatar | ApiCountry | ApiInaccurateLocation | ApiLocality | ApiLocation | ApiPerk | ApiPlace

const ENTITY_TYPES = ['avatars', 'countries', 'inaccurate_locations', 'localities', 'locations', 'perks', 'places'] as const

const EMPTY_ENTITIES: EntitiesState = {}
